/**
 * Bookmarks page — lists the current user's bookmarks across all content types.
 * Pinned items appear first; supports filtering by item type.
 */
import { ReactElement, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bookmark as BookmarkIcon,
  Pin,
  PinOff,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import {
  bookmarksAPI,
  Bookmark,
  BookmarkItemType,
  BookmarkListResponse,
} from '../services/api';

// Display labels for each item type. Mirrors backend ITEM_TYPE_DISPLAY.
const TYPE_LABEL: Record<BookmarkItemType, string> = {
  news_economic: 'Economic',
  news_earnings: 'Markets',
  news_events: 'Events',
  report: 'Report',
  trading: 'Trading',
  history: 'History',
  stock: 'Stock',
};

const TYPE_FILTERS: { key: BookmarkItemType | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'news_economic', label: 'Economic' },
  { key: 'news_earnings', label: 'Markets' },
  { key: 'news_events', label: 'Events' },
  { key: 'report', label: 'Reports' },
  { key: 'trading', label: 'Trading' },
  { key: 'history', label: 'History' },
  { key: 'stock', label: 'Stocks' },
];

function isAbsolute(url: string): boolean {
  return /^https?:\/\//.test(url);
}

function BookmarkRow({ bm }: { bm: Bookmark }): ReactElement {
  const queryClient = useQueryClient();

  const pinMutation = useMutation({
    mutationFn: async () => {
      const res = bm.is_pinned
        ? await bookmarksAPI.unpin(bm.id)
        : await bookmarksAPI.pin(bm.id);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookmarks-list'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await bookmarksAPI.deleteById(bm.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookmarks-list'] });
      queryClient.invalidateQueries({ queryKey: ['bookmark-check', bm.item_type, bm.item_id] });
    },
  });

  const url = bm.url ?? '#';
  const isExternal = isAbsolute(url) || url.startsWith('/api/');
  const titleEl = (
    <h3
      className="text-sm sm:text-base font-semibold text-gray-900 group-hover:text-blue-700 transition-colors leading-snug line-clamp-2"
      style={{ fontFamily: 'Georgia, serif' }}
    >
      {bm.title || `${TYPE_LABEL[bm.item_type]} (${bm.item_id})`}
    </h3>
  );

  return (
    <div
      className={`group relative bg-white border rounded-lg p-3.5 sm:p-4 transition-all ${
        bm.is_pinned
          ? 'border-amber-300 ring-1 ring-amber-100'
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-600 bg-slate-100 rounded">
              {TYPE_LABEL[bm.item_type]}
            </span>
            {bm.is_pinned && (
              <span className="px-1.5 py-0.5 text-[10px] font-medium text-amber-700 bg-amber-50 rounded flex items-center gap-0.5">
                <Pin className="w-2.5 h-2.5" />
                Pinned
              </span>
            )}
            <span className="text-[10px] text-gray-400 ml-auto">
              {new Date(bm.created_at).toLocaleDateString()}
            </span>
          </div>
          {isExternal ? (
            <a href={url} className="group block">
              {titleEl}
            </a>
          ) : (
            <Link to={url} className="group block">
              {titleEl}
            </Link>
          )}
          {bm.subtitle && (
            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{bm.subtitle}</p>
          )}
        </div>
        <div className="flex flex-col gap-1 flex-shrink-0">
          <button
            onClick={() => pinMutation.mutate()}
            disabled={pinMutation.isPending}
            className="p-1.5 rounded-md text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors disabled:opacity-50"
            title={bm.is_pinned ? 'Unpin' : 'Pin'}
            aria-label={bm.is_pinned ? 'Unpin bookmark' : 'Pin bookmark'}
          >
            {pinMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : bm.is_pinned ? (
              <PinOff className="w-4 h-4" />
            ) : (
              <Pin className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={() => {
              if (window.confirm('Remove this bookmark?')) {
                deleteMutation.mutate();
              }
            }}
            disabled={deleteMutation.isPending}
            className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
            title="Remove"
            aria-label="Remove bookmark"
          >
            {deleteMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Bookmarks(): ReactElement {
  const [filter, setFilter] = useState<BookmarkItemType | 'all'>('all');
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const { data, isLoading, error } = useQuery<BookmarkListResponse>({
    queryKey: ['bookmarks-list', filter, page],
    queryFn: async () => {
      const res = await bookmarksAPI.list({
        item_type: filter === 'all' ? undefined : filter,
        limit: pageSize,
        offset: page * pageSize,
      });
      return res.data;
    },
    staleTime: 30_000,
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <header className="mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-3 mb-1">
            <BookmarkIcon className="w-6 h-6 text-amber-600" />
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>
              My Bookmarks
            </h1>
          </div>
          <p className="text-sm text-gray-500">
            Saved articles, reports, stocks, and more.
          </p>
        </header>

        {/* Filter tabs */}
        <div className="flex items-center gap-1.5 mb-5 overflow-x-auto pb-1">
          {TYPE_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => {
                setFilter(f.key);
                setPage(0);
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                filter === f.key
                  ? 'bg-slate-800 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400 mb-3" />
            <p className="text-sm text-gray-500">Loading bookmarks...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-center py-20">
            <ExternalLink className="w-8 h-8 text-red-400 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">Failed to load bookmarks</h3>
            <p className="text-sm text-gray-500">Please sign in or try again later</p>
          </div>
        )}

        {/* List */}
        {data && !isLoading && (
          <>
            {data.bookmarks.length === 0 ? (
              <div className="text-center py-20 bg-white border border-gray-100 rounded-lg">
                <BookmarkIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <h3 className="text-base font-medium text-gray-700 mb-1">
                  {filter === 'all' ? 'No bookmarks yet' : `No ${TYPE_LABEL[filter as BookmarkItemType]} bookmarks`}
                </h3>
                <p className="text-sm text-gray-500">
                  Tap the bookmark icon on any article or stock to save it here.
                </p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {data.bookmarks.map((bm) => (
                  <BookmarkRow key={bm.id} bm={bm} />
                ))}
              </div>
            )}

            {/* Pagination */}
            {data.total > pageSize && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="px-3 py-1.5 text-sm text-gray-600">
                  Page {page + 1} of {Math.ceil(data.total / pageSize)}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!data.has_more}
                  className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
